import { useEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/router'

import { saveAs } from 'file-saver'

import DepositCard from 'pages/account/components/deposit-card'
import { LoanCard } from 'pages/account/components/deposit-card/loan-card'
import FilterButton from 'pages/account/components/deposit-card/header/filter-button'
import { AddFilterPopup } from 'pages/account/components/add-filter-popup'

import ContentHeader from 'components/ui/account/content/__header'

import DownloadIcon from 'images/icon-download.svg'

import { useAppSelector } from 'store/hooks'
import { fetchUserLoans } from 'store/fetchers/loans'
import { ELoanStatuses, IFormattedLoan } from 'store/fetchers/loans/enum'
import { LoginStatuses, selectedAuthStatus, selectedUser } from 'store/reducers/users'
import useOnClickOutside from 'hooks/use-on-click-outside'
import { useHeader } from 'utils/use-header'

import StatusList, { EZones } from 'pages/account/components/status-list'

import { LinkButton } from 'components/ui/link-button'

import { UTCFormatDate } from 'components/ui/transaction-history/components/transaction/utils'

import Skeleton from 'components/ui/skeleton'
import Search from 'components/ui/search'

import cn from 'classnames'

import { useTranslation } from 'utils/use-translation'

import { Links } from '../components/actions/enum'

import { FilterDataType, IFilterData, addFilterData, removeFilterData } from '../../../utils/filter-data'

import FilterTooltip from '../../../components/ui/account/filter-tooltip'

import EmptyIcon from './icons/empty.svg'

import { filterItems, hasDayPassed, LoanStatus, sortByAssetName, sortByDate, sortByMarginCall } from './utils'

import styles from './styles.module.scss'
import LiquidationPortfolio from 'components/ui/account/liquidation-portfolio'

const headerCSVRows =
  [
    'Created at',
    'Loan ID',
    'Collateral amount',
    'Collateral currency',
    'Collateral network',
    'Loan amount',
    'Loan currency',
    'Loan network',
    'Margin call',
    'Status',
    'Total repayment amount',
    'Closed at',
    'Liquidated at',
  ].join(',') + '\r\n'

const zoneMapping = {
  [EZones.GREEN]: [3],
  [EZones.ORANGE]: [1, 2],
  [EZones.RED]: [0, -1],
}

const Loans = () => {
  const { t } = useTranslation()

  const [loans, setLoans] = useState<IFormattedLoan[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchValue, setSearchValue] = useState('')
  const [statusFilter, setStatusFilter] = useState<LoanStatus[]>([])
  const [zonesFilter, setZonesFilter] = useState<EZones[]>([])
  const [selectedFilter, setSelectedFilter] = useState(filterItems.allLoans)
  const [filterPopupIsVisible, setFilterPopupIsVisible] = useState(false)

  const [tickersFilter, setTickersFilter] = useState<string[]>([])
  const [assetNameSorting, setAssetNameSorting] = useState<number>(0)
  const [marginCallSorting, setMarginCallSorting] = useState<number>(0)
  const [dateSorting, setDateSorting] = useState<number>(2)
  const [statusesModalOpened, setStatusesModalOpened] = useState(false)
  const [filterData, setFilterData] = useState<IFilterData[]>([])
  const [isFirstVisited, setIsFirstVisited] = useState<boolean>(true)

  const statusesListRef = useRef(null)

  const authStatus = useAppSelector(selectedAuthStatus)
  const user = useAppSelector(selectedUser)

  const head = useHeader(t('loans_dashboard'))
  const router = useRouter()

  useOnClickOutside(statusesListRef, () => setStatusesModalOpened(false))

  useEffect(() => {
    if (typeof window !== 'undefined' && 'filt' in window) {
      window.location.reload()
    }
  }, [])

  const saveCSV = () => {
    const rows = []

    filteredLoans.forEach((data) => {
      const { created_at, loan_id, loan, status, deposit, liquidation_price, closed_at, liquidated_at, repayment } = data
      if (
        status !== ELoanStatuses.ORDER_CREATED &&
        status !== ELoanStatuses.CLOSED &&
        status !== ELoanStatuses.LIQUIDATED
      ) {
        return
      }
      const res =
        [
          UTCFormatDate(created_at),
          loan_id,
          deposit.amount,
          deposit.currency_code,
          deposit.currency_network,
          loan.amount,
          loan.currency_code,
          loan.currency_network,
          liquidation_price,
          status,
          repayment?.total_amount ?? 0,
          UTCFormatDate(closed_at),
          UTCFormatDate(liquidated_at),
        ].join(',') + '\r\n'

      rows.push(res)
    })

    saveAs(new Blob([headerCSVRows, ...rows], { type: 'text/csv;charset=utf-8' }), 'CoinRabbit_Loan_statements.csv')
  }

  const searchTextOnChange = (searchValue: string) => {
    setSearchValue(searchValue)
  }

  const filteredLoans = useMemo(() => {
    if (!loans?.length) return []
    try {
      setSelectedFilter(filterItems.allLoans)
      let filteredList = null

      filteredList = sortByMarginCall(loans, marginCallSorting)

      filteredList = filteredList.filter((loan) => {
        return !(loan.status === ELoanStatuses.CONFIRMED && hasDayPassed(loan.created_at))
      })

      filteredList = sortByDate(filteredList, dateSorting)

      filteredList = sortByAssetName(filteredList, assetNameSorting)

      if (tickersFilter.length) {
        filteredList = filteredList.filter((loan) => tickersFilter.includes(loan.deposit.currency_code.toLowerCase()))
      }

      if (searchValue) {
        filteredList = filteredList.filter((loan) => {
          const checkCode = loan.deposit.currency_code.toLowerCase().includes(searchValue.trim().toLowerCase())
          const checkNetwork = loan.deposit.currency_network.toLowerCase().includes(searchValue.trim().toLowerCase())

          const checkId = loan.loan_id.toLowerCase().includes(searchValue.trim().toLowerCase())

          if (checkCode || checkNetwork || checkId) return loan

          return null
        })
      }

      if (selectedFilter.statuses.length !== 0) {
        filteredList = filteredList.filter((i) => selectedFilter.statuses?.includes(i.status))
      }

      if (zonesFilter.length !== 0) {
        const allowedZones = zonesFilter.flatMap((zone) => zoneMapping[zone] || [])

        filteredList = filteredList.filter((i) => allowedZones.includes(i.current_zone))
      }
      return filteredList
    } catch (error) {
      return []
    }
  }, [
    loans,
    statusFilter,
    selectedFilter,
    marginCallSorting,
    tickersFilter,
    searchValue,
    assetNameSorting,
    dateSorting,
    zonesFilter,
  ])

  const handleStatusSelect = (status: LoanStatus, zone?: EZones | EZones[]) => {
    setFilterData((prevFilterData) => {
      let filterDataArr = [...prevFilterData]

      if (
        !statusFilter.includes(status) ||
        (zone && zonesFilter.length !== 0 && status === LoanStatus.Active) ||
        (!zone && zonesFilter.length !== 3 && status === LoanStatus.Active)
      ) {
        if (status === LoanStatus.Active) {
          if (Array.isArray(zone)) {
            let newZones = [...zonesFilter]

            for (const el of zone) {
              if (!newZones.includes(el)) {
                newZones.push(el)
              } else {
                newZones = newZones.filter((item) => item !== el)
              }
            }

            if (newZones.length === 3) {
              const data: IFilterData = {
                type: FilterDataType.STATUS,
                content: status,
                data: status,
                onClick: () => {
                  setStatusFilter([])
                  setZonesFilter([])
                  setFilterData((prevData) => prevData.filter((item) => item.type !== FilterDataType.STATUS))
                  setSelectedFilter(filterItems.allLoans)
                  selectedFilter.statuses = []
                },
                onDestroy: null,
                filters: [status],
                count: 1,
              }
              filterDataArr = filterDataArr.filter((item) => item.type !== FilterDataType.ZONE)
              filterDataArr = addFilterData(filterDataArr, data, t)
            } else {
              for (const item of zone) {
                if (!zonesFilter.includes(item)) {
                  const data: IFilterData = {
                    type: FilterDataType.ZONE,
                    content: item,
                    data: item,
                    onClick: () => {
                      setZonesFilter([])
                      setStatusFilter((prev) => prev.filter((status) => status !== LoanStatus.Active))
                      setFilterData((prev) => prev.filter((item) => item.type !== FilterDataType.ZONE))
                      selectedFilter.statuses = selectedFilter.statuses.filter(
                        (el) => el !== ELoanStatuses.ORDER_CREATED && el !== ELoanStatuses.LOAN_FAILED,
                      )
                    },
                    onDestroy: null,
                    filters: [item],
                    count: 1,
                  }

                  filterDataArr = addFilterData(filterDataArr, data, t)
                } else {
                  if (zonesFilter.length === 3) {
                    for (const filter of zonesFilter) {
                      const data: IFilterData = {
                        type: FilterDataType.ZONE,
                        content: filter,
                        data: filter,
                        onClick: () => {
                          setZonesFilter([])
                          setStatusFilter((prev) => prev.filter((status) => status !== LoanStatus.Active))
                          setFilterData((prev) => prev.filter((item) => item.type !== FilterDataType.ZONE))
                          selectedFilter.statuses = selectedFilter.statuses.filter(
                            (el) => el !== ELoanStatuses.ORDER_CREATED && el !== ELoanStatuses.LOAN_FAILED,
                          )
                        },
                        onDestroy: null,
                        filters: [filter],
                        count: 1,
                      }

                      filterDataArr = addFilterData(filterDataArr, data, t)
                    }
                    const dataToRemove: IFilterData = {
                      type: FilterDataType.STATUS,
                      content: statusFilter.find((item) => item !== LoanStatus.Active),
                      data: statusFilter.find((item) => item !== LoanStatus.Active),
                      onClick: () => {
                        return null
                      },
                      onDestroy: null,
                      filters: [LoanStatus.Active],
                      count: 1,
                    }

                    filterDataArr = removeFilterData(filterDataArr, dataToRemove, t)
                  }

                  const data: IFilterData = {
                    type: FilterDataType.ZONE,
                    content: item,
                    data: item,
                    onClick: () => {
                      setZonesFilter([])
                      setStatusFilter((prev) => prev.filter((status) => status !== LoanStatus.Active))
                      setFilterData((prev) => prev.filter((item) => item.type !== FilterDataType.ZONE))
                      selectedFilter.statuses = selectedFilter.statuses.filter(
                        (el) => el !== ELoanStatuses.ORDER_CREATED && el !== ELoanStatuses.LOAN_FAILED,
                      )
                    },
                    onDestroy: null,
                    filters: zonesFilter.filter((el) => zone.includes(el)),
                    count: 1,
                  }

                  filterDataArr = removeFilterData(filterDataArr, data, t)
                }
              }
            }
          } else {
            if (!zonesFilter.includes(zone)) {
              const newZones = [...zonesFilter]
              newZones.push(zone)

              if (newZones.length === 3 || !zone) {
                const data: IFilterData = {
                  type: FilterDataType.STATUS,
                  content: t(status.toLowerCase()),
                  data: status,
                  onClick: () => {
                    setStatusFilter([])
                    setZonesFilter([])
                    setFilterData((prevData) => prevData.filter((item) => item.type !== FilterDataType.STATUS))
                    setSelectedFilter(filterItems.allLoans)
                    selectedFilter.statuses = []
                  },
                  onDestroy: null,
                  filters: [status],
                  count: 1,
                }
                filterDataArr = filterDataArr.filter((item) => item.type !== FilterDataType.ZONE)

                filterDataArr = addFilterData(filterDataArr, data, t)
              } else {
                const data: IFilterData = {
                  type: FilterDataType.ZONE,
                  content: zone,
                  data: zone,
                  onClick: () => {
                    setZonesFilter([])
                    setStatusFilter((prev) => prev.filter((status) => status !== LoanStatus.Active))
                    setFilterData((prevData) => prevData.filter((item) => item.type !== FilterDataType.ZONE))
                    selectedFilter.statuses = selectedFilter.statuses.filter(
                      (el) => el !== ELoanStatuses.ORDER_CREATED && el !== ELoanStatuses.LOAN_FAILED,
                    )
                  },
                  onDestroy: null,
                  filters: [zone],
                  count: 1,
                }

                filterDataArr = addFilterData(filterDataArr, data, t)
              }
            } else {
              if (!filterDataArr.find((item) => item.type === FilterDataType.ZONE)) {
                for (const filter of zonesFilter) {
                  const data: IFilterData = {
                    type: FilterDataType.ZONE,
                    content: filter,
                    data: filter,
                    onClick: () => {
                      setZonesFilter([])
                      setStatusFilter((prev) => prev.filter((status) => status !== LoanStatus.Active))
                      setFilterData((prev) => prev.filter((item) => item.type !== FilterDataType.ZONE))
                      selectedFilter.statuses = selectedFilter.statuses.filter(
                        (el) => el !== ELoanStatuses.ORDER_CREATED && el !== ELoanStatuses.LOAN_FAILED,
                      )
                    },
                    onDestroy: null,
                    filters: [filter],
                    count: 1,
                  }

                  filterDataArr = addFilterData(filterDataArr, data, t)
                }

                const dataToRemove: IFilterData = {
                  type: FilterDataType.STATUS,
                  content: statusFilter.find((item) => item !== LoanStatus.Active),
                  data: statusFilter.find((item) => item !== LoanStatus.Active),
                  onClick: () => {
                    return null
                  },
                  onDestroy: null,
                  filters: [LoanStatus.Active],
                  count: 1,
                }
                filterDataArr = removeFilterData(filterDataArr, dataToRemove, t)
              }

              const data: IFilterData = {
                type: FilterDataType.ZONE,
                content: zonesFilter.find((item) => item !== zone),
                data: zonesFilter.find((item) => item !== zone),
                onClick: () => {
                  return null
                },
                onDestroy: null,
                filters: [zone],
                count: 1,
              }

              filterDataArr = removeFilterData(filterDataArr, data, t)
            }
          }
        } else {
          const data: IFilterData = {
            type: FilterDataType.STATUS,
            content: t(status.toLowerCase()),
            data: status,
            onClick: () => {
              setStatusFilter([])
              setZonesFilter([])
              setFilterData((prevData) => prevData.filter((item) => item.type !== FilterDataType.STATUS))
              setSelectedFilter(filterItems.allLoans)
              selectedFilter.statuses = []
            },
            onDestroy: null,
            filters: [status],
            count: 1,
          }

          filterDataArr = addFilterData(filterDataArr, data, t)
        }
      } else {
        const data: IFilterData = {
          type: FilterDataType.STATUS,
          content: t(statusFilter.find((item) => item !== status)?.toLowerCase()),
          data: statusFilter.find((item) => item !== status),
          onClick: () => {
            return null
          },
          onDestroy: null,
          filters: [status],
          count: 1,
        }

        filterDataArr = removeFilterData(filterDataArr, data, t)
      }

      if (filterDataArr.length >= 2 && !filterDataArr.find((item) => item.type === null)) {
        const data: IFilterData = {
          type: null,
          content: t('clear_all_filters'),
          data: 'Clear all filters',
          count: 0,
          filters: [],
          onClick: () => {
            setFilterData([])
            setStatusFilter([])
            setZonesFilter([])
          },
          onDestroy: () => {
            data.isDestroying = true
            setTimeout(() => {
              data.onClick()
              data.isDestroying = false
            }, 300)
          },
        }
        filterDataArr = addFilterData(filterDataArr, data, t)
      }

      return filterDataArr
    })

    setStatusFilter((prevStatusFilter) => {
      let newStatusFilter = [...prevStatusFilter]

      if (!newStatusFilter.includes(status)) {
        newStatusFilter.push(status)

        switch (status) {
          case LoanStatus.Active:
            if (!selectedFilter.statuses.includes(ELoanStatuses.ORDER_CREATED)) {
              selectedFilter.statuses.push(ELoanStatuses.ORDER_CREATED)
            }
            if (!selectedFilter.statuses.includes(ELoanStatuses.LOAN_FAILED)) {
              selectedFilter.statuses.push(ELoanStatuses.LOAN_FAILED)
            }
            break
          case LoanStatus.Closed:
            if (!selectedFilter.statuses.includes(ELoanStatuses.CLOSED)) {
              selectedFilter.statuses.push(ELoanStatuses.CLOSED)
            }
            break
          case LoanStatus.Liquidated:
            if (!selectedFilter.statuses.includes(ELoanStatuses.LIQUIDATED)) {
              selectedFilter.statuses.push(ELoanStatuses.LIQUIDATED)
            }
            break
          default:
            break
        }
      } else {
        const set = new Set(newStatusFilter)
        set.delete(status)

        if (Array.isArray(zone)) {
          if (
            (status?.toLowerCase() === 'active' &&
              zone.length !== 0 &&
              zonesFilter.filter((item) => !zone.includes(item)).length !== 0) ||
            (status?.toLowerCase() === 'active' &&
              zone.length === 0 &&
              zonesFilter.length !== 0 &&
              zonesFilter.length !== 3)
          ) {
            return newStatusFilter
          } else {
            newStatusFilter = Array.from(set)
          }
        } else {
          if (
            (status?.toLowerCase() === 'active' && zone && zonesFilter.filter((item) => item !== zone).length !== 0) ||
            (status?.toLowerCase() === 'active' && !zone && zonesFilter.length !== 0 && zonesFilter.length !== 3)
          ) {
            return newStatusFilter
          } else {
            newStatusFilter = Array.from(set)
          }
        }

        switch (status) {
          case LoanStatus.Active:
            selectedFilter.statuses = selectedFilter.statuses.filter(
              (status) => status !== ELoanStatuses.ORDER_CREATED && status !== ELoanStatuses.LOAN_FAILED,
            )
            break
          case LoanStatus.Closed:
            selectedFilter.statuses = selectedFilter.statuses.filter((status) => status !== ELoanStatuses.CLOSED)
            break
          case LoanStatus.Liquidated:
            selectedFilter.statuses = selectedFilter.statuses.filter((status) => status !== ELoanStatuses.LIQUIDATED)
            break
          default:
            break
        }
      }
      return newStatusFilter
    })

    if (zone && status === LoanStatus.Active) {
      if (Array.isArray(zone)) {
        setZonesFilter((prev) => {
          let newZones = [...prev]
          zone.forEach((singleZone) => {
            if (!prev.includes(singleZone)) {
              newZones.push(singleZone)
            } else {
              newZones = newZones.filter((item) => item !== singleZone)
            }
          })
          return newZones
        })
      } else {
        setZonesFilter((prevZones) => {
          let newZones = [...prevZones]

          if (!newZones.includes(zone)) {
            newZones.push(zone)
          } else {
            newZones = newZones.filter((item) => item !== zone)
          }
          return newZones
        })
      }
    } else {
      if (zonesFilter.length !== 3 && status === LoanStatus.Active) {
        setZonesFilter([EZones.GREEN, EZones.ORANGE, EZones.RED])
      } else {
        if (status === LoanStatus.Active) {
          setZonesFilter([])
        }
      }
    }
    setIsFirstVisited(false)
  }

  const saveStatusFilterStateToLocal = (statusFilterState) => {
    localStorage.setItem('loans::statusFilter', JSON.stringify(statusFilterState))
  }

  const saveZonesFilterStateToLocal = (zonesFilterState) => {
    localStorage.setItem('loans::zonesFilter', JSON.stringify(zonesFilterState))
  }

  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const savedStatusFilterState = JSON.parse(localStorage?.getItem('loans::statusFilter'))
      const savedZonesFilterState = JSON.parse(localStorage?.getItem('loans::zonesFilter'))
      if (savedStatusFilterState && Array.isArray(savedStatusFilterState)) {
        savedStatusFilterState.forEach((status) => handleStatusSelect(status, savedZonesFilterState))
      }
    }
  }, [])

  useEffect(() => {
    if (!isFirstVisited) {
      saveStatusFilterStateToLocal(statusFilter)
    }
  }, [statusFilter])

  useEffect(() => {
    if (!isFirstVisited) {
      saveZonesFilterStateToLocal(zonesFilter)
    }
  }, [zonesFilter])

  useEffect(() => {
    async function fetcher() {
      try {
        const fetchedLoans = await fetchUserLoans()
        const sortedLoans = fetchedLoans.sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        )
        setLoans(sortedLoans)
        setIsLoading(false)
      } catch (error) {
        setIsLoading(false)
      }
    }

    if (authStatus === LoginStatuses.LOGIN) {
      void fetcher()
    }
  }, [authStatus])

  useEffect(() => {
    if (
      !isLoading &&
      (!loans?.length ||
        !loans.filter((loan) => {
          return !(loan.status === ELoanStatuses.CONFIRMED && hasDayPassed(loan.created_at))
        }).length)
    ) {
      void router.push('/account/loans/add?hide-back=true')
    }
  }, [isLoading, loans])

  return (
    <>
      {head}
      <section className={styles.wrapper}>
        <div className={styles.loans__header}>
          <ContentHeader title={t('loans')} className={styles.loans__heading}>
            <div className={styles.loans__head}>
              <img src={DownloadIcon} className={styles.downloadIcon} alt="icon download" />
              <p onClick={saveCSV}>{t('download_csv')}</p>
            </div>
          </ContentHeader>

          <LinkButton title={'+ ' + t('add_new_loan')} path={Links.NEW_LOAN} />

          {user?.is_liquidation_portfolio_enabled && <LiquidationPortfolio
            loans={loans}
          />}
        </div>

        <div className={styles.list__header}>
          <div className={styles.searchAndFilters}>
            <Search
              className={styles.search}
              onChange={searchTextOnChange}
              value={searchValue}
              placeholder={t('search_by_asset')}
            />

            <button className={styles.csvBtn} onClick={saveCSV}>
              <img src={DownloadIcon} className={styles.downloadIcon} alt="icon download" />
              <p onClick={saveCSV}>{t('download_csv')}</p>
            </button>

            <FilterButton onClick={() => setFilterPopupIsVisible(true)} className={styles.filterIcon} isActive={true} />
            {filterPopupIsVisible && (
              <AddFilterPopup
                onClose={() => setFilterPopupIsVisible(false)}
                statusFilter={{
                  statuses: [LoanStatus.Active, LoanStatus.Closed, LoanStatus.Liquidated],
                  selectedStatuses: statusFilter,
                  onSelect: handleStatusSelect,
                  selectedZones: zonesFilter,
                }}
                sortings={[
                  { label: t('assets'), state: assetNameSorting, setState: setAssetNameSorting },
                  { label: t('date'), state: dateSorting, setState: setDateSorting },
                  { label: t('margin_call'), state: marginCallSorting, setState: setMarginCallSorting },
                ]}
                isLoan
              />
            )}
          </div>

          <div
            className={cn(styles.filterTooltips, {
              [styles.filterTooltips_noempty]: new Array(...new Set(filterData)).length !== 0,
            })}
          >
            {new Array(...new Set(filterData)).map((item, index) => (
              <FilterTooltip
                imgSrc={item.imgSrc}
                key={index}
                content={item.content}
                onClose={item.onClick}
                type={item.type}
                isDestroying={item.isDestroying}
              />
            ))}
          </div>

          <DepositCard.ColumnsHeader>
            <DepositCard.ColumnHeaderCell
              title={t('collateral_capital')}
              onClick={() => {
                setDateSorting(0)
                setMarginCallSorting(0)
                setAssetNameSorting((prev) => (prev === 1 ? 2 : 1))
              }}
              sortingState={assetNameSorting}
            />

            <DepositCard.ColumnHeaderCell
              title={t('date')}
              onClick={() => {
                setAssetNameSorting(0)
                setMarginCallSorting(0)
                setDateSorting((prev) => (prev === 1 ? 2 : 1))
              }}
              sortingState={dateSorting}
            />

            <DepositCard.ColumnHeaderCell title={t('current_rate')} />

            <DepositCard.ColumnHeaderCell
              title={t('margin_call')}
              onClick={() => {
                setAssetNameSorting(0)
                setDateSorting(0)
                setMarginCallSorting((prev) => (prev === 1 ? 2 : 1))
              }}
              sortingState={marginCallSorting}
            />

            <div ref={statusesListRef} style={{ position: 'relative' }}>
              <DepositCard.ColumnHeaderCell
                title={t('status')}
                onClick={() => setStatusesModalOpened(!statusesModalOpened)}
                isActive={statusFilter.length > 0 || zonesFilter.length > 0 || !!searchValue}
                className={styles.last}
                onClearSort={() => {
                  setStatusFilter([])
                  setZonesFilter([])
                  setStatusesModalOpened(false)
                  setSearchValue('')
                  setSelectedFilter(filterItems.allLoans)
                  selectedFilter.statuses = []
                  setFilterData([])
                }}
              />
              {statusesModalOpened && (
                <StatusList
                  statuses={[LoanStatus.Active, LoanStatus.Closed, LoanStatus.Liquidated]}
                  selectedStatuses={statusFilter}
                  onSelect={handleStatusSelect}
                  isLoan
                  selectedZones={zonesFilter}
                />
              )}
            </div>
          </DepositCard.ColumnsHeader>
        </div>

        {!isLoading && !!loans?.length && (
          <>
            <div className={styles.container} style={{ height: filteredLoans.length === 0 ? '100%' : undefined }}>
              <div className={styles.list}>
                {isLoading && (
                  <div className={styles.skeletons}>
                    {new Array(6).fill(1).map((_, i) => (
                      <Skeleton key={i} />
                    ))}
                  </div>
                )}

                {!isLoading &&
                  filteredLoans
                    .filter((item) => item.status !== ELoanStatuses.NEW)
                    .map((loan) => <LoanCard loan={loan} key={loan?.loan_id} />)}

                {!isLoading && !filteredLoans?.length && (
                  <div className={styles.emptyList}>
                    <img src={EmptyIcon} alt="empty icon" />
                    <div className={styles.emptyList__data}>
                      <p>{t('no_matching_loans')}</p>
                      <button
                        onClick={() => {
                          setTickersFilter([])
                          setFilterData([])
                          setMarginCallSorting(0)
                          setStatusFilter([])
                          setSelectedFilter(filterItems.allLoans)
                          setSearchValue('')
                          setZonesFilter([])
                          selectedFilter.statuses = []
                        }}
                        className={styles.clear}
                      >
                        {t('clear_filters')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </section>
    </>
  )
}

export default Loans
